import api from './api';

// ========== DTOs ==========
export interface UserDTO {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  userType: string;
  status: string;
  dateOfBirth?: string;
  schoolName?: string;
  gradeLevel?: string;
  profilePictureUrl?: string;
  createdAt?: string;
  updatedAt?: string;
  lastLoginAt?: string;
  emailVerified: boolean;
  phoneVerified: boolean;
  isSuperAdmin?: boolean;
  createdBy?: number;
}

export interface UserStatusUpdateRequest {
  status: 'ACTIVE' | 'INACTIVE' | 'PENDING' | 'SUSPENDED' | 'DELETED';
}

export type UserType = 'STUDENT' | 'PARENT' | 'TEACHER' | 'TUTOR' | 'SYSTEM_ADMIN' | 'SCHOOL_ADMIN';
export type UserStatus = 'ACTIVE' | 'INACTIVE' | 'PENDING' | 'SUSPENDED' | 'DELETED';

// ========== API Functions ==========

/**
 * Get users by type (non-STUDENT/PARENT types only, or paginated for STUDENT/PARENT)
 * Note: STUDENT and PARENT types require pagination and will throw an error
 */
export const getUsersByType = async (userType: UserType): Promise<UserDTO[]> => {
  try {
    const response = await api.get(`/api/users/type/${userType}`);
    return response.data.data || [];
  } catch (error: any) {
    // If STUDENT or PARENT type is requested, return empty array with a warning
    if (userType === 'STUDENT' || userType === 'PARENT') {
      console.warn(`Cannot retrieve all ${userType} users without pagination. Returning empty array.`);
      return [];
    }
    throw error;
  }
};

/**
 * Get user by ID
 */
export const getUserById = async (userId: number): Promise<UserDTO> => {
  const response = await api.get(`/api/users/${userId}`);
  return response.data.data;
};

/**
 * Get user by email
 */
export const getUserByEmail = async (email: string): Promise<UserDTO> => {
  const response = await api.get(`/api/users/email/${encodeURIComponent(email)}`);
  return response.data.data;
};

/**
 * Update user status
 * Only SYSTEM_ADMIN or SCHOOL_ADMIN can update user status
 */
export const updateUserStatus = async (
  userId: number,
  status: UserStatus
): Promise<UserDTO> => {
  const response = await api.put(`/api/users/${userId}/status`, null, {
    params: { status },
  });
  return response.data.data;
};

/**
 * Get all users (for admin - may need pagination for large datasets)
 * This is a helper that tries to get users by type
 */
export const getAllUsers = async (): Promise<UserDTO[]> => {
  // For now, we'll get non-STUDENT/PARENT types
  // In production, this should use pagination
  const types: UserType[] = ['TEACHER', 'TUTOR', 'SYSTEM_ADMIN', 'SCHOOL_ADMIN'];
  const allUsers: UserDTO[] = [];
  
  for (const type of types) {
    try {
      const users = await getUsersByType(type);
      allUsers.push(...users);
    } catch (error) {
      // Skip types that fail (e.g., if no users of that type exist)
      console.warn(`Failed to fetch users of type ${type}:`, error);
    }
  }
  
  return allUsers;
};

